import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { addDoc, collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import pick from "lodash/pick";
import { RootState } from "@/store/store";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { fetchUsers } from "@/store/usersSlice";
import { Content, ContentType, DraftContent, NewContent, NewOffer, Offer } from "@/types";
import { addOrganizationContent } from "../slice";
import { ApiError } from "@/types/api";

export const contentApi = createApi({
  reducerPath: "content",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["Content"],
  endpoints: (builder) => ({
    fetchContent: builder.query<Content[], string | SkipToken>({
      queryFn: async (organizationId: string, { dispatch }) => {
        const TASK = "Contenido";

        if (!organizationId) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        try {
          dispatch(addLoadingTask(TASK));

          const collectionRef = collection(db, `organizations/${String(organizationId)}/content`);

          const content = await getDocs(collectionRef).then((querySnapshot) => {
            return querySnapshot.docs.reduce((acc, doc: any) => {
              return [...acc, { ...doc.data(), id: doc.id }];
            }, [] as Content[]);
          });

          if (!content.length) {
            return {
              error: {
                status: 404,
                statusText: "Contenido no encontrado",
              },
            };
          }

          // get the details of every user that created the content
          const userIds = content.map((item) => item.createdBy);
          const uniqueUserIds = [...new Set(userIds)];
          dispatch(fetchUsers(uniqueUserIds));
          dispatch(addOrganizationContent({ organizationId, content }));

          return { data: content };
        } catch {
          return {
            error: {
              status: 500,
              statusText: "Error al obtener el contenido",
            },
          }
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      providesTags: ["Content"],
    }),
    addContent: builder.mutation<Content, string | SkipToken>({
      queryFn: async ({ draft, organizationId }: { draft: DraftContent; organizationId: string }, { dispatch, getState }) => {
        const TASK = "Crear contenido";

        if (!organizationId || !draft) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        const store = getState() as RootState;

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${String(organizationId)}/content`);

          const ALLOWED_FIELDS = ["title", "previewDescription", "description", "price", "images", "category", "elements", "status", "order"];

          const payload: Partial<Content> = {
            ...pick(draft, ALLOWED_FIELDS),
            createdBy: store.auth.user.uid,
            createdAt: Date.now(),
          };

          const doc = await addDoc(collectionRef, payload);
          
          return {
            data: {
              ...payload,
              id: doc.id,
            }
          }
        } catch (error) {
          return {
            error: {
              status: 500,
              statusText: "Error al crear el contenido",
            },
          }
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Content"],
    }),
    updateContent: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
          content,
        }: {
          organizationId: string | SkipToken;
          contentId: string;
          content: Content;
        },
        { getState, dispatch }
      ) => {
        const TASK = "Actualizando contenido";

        const ALLOWED_FIELDS = ["title", "description", "previewDescription", "price", "images", "category", "status", "quantity", "available", "order", "elements"];

        try {
          dispatch(addLoadingTask(TASK));

          const store = getState() as RootState;

          if (!store.auth?.user?.uid || !organizationId || !contentId) {
            throw new Error("Invalid payload");
          }
          const docRef = doc(db, `organizations/${String(organizationId)}/content/${String(contentId)}`);

          const payload = {
            ...pick(content, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          const response = await updateDoc(docRef, payload);
          return { data: response };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Content"],
    }),

    addOffer: builder.mutation({
      queryFn: async ({ draft, organizationId }: { draft: NewOffer; organizationId: string }, { dispatch, getState }) => {
        const TASK = "Crear Oferta";
        const store = getState() as RootState;

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${String(organizationId)}/content`);

          const ALLOWED_FIELDS = ["createdAt", "createdBy", "title", "images", "status", "type", "region", "component"];

          const payload: NewOffer = {
            ...pick(draft, ALLOWED_FIELDS),
            type: ContentType.OFFER,
            createdBy: store.auth.user.uid,
            createdAt: Date.now(),
          };

          const doc = await addDoc(collectionRef, payload);
          return { data: doc as unknown as NewOffer };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Content"],
    }),
    updateOffer: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
          content,
        }: {
          organizationId: string | SkipToken;
          contentId: string;
          content: Offer;
        },
        { getState, dispatch }
      ) => {
        const TASK = "Actualizando Oferta";
        const ALLOWED_FIELDS = ["title", "images", "status", "type", "lastUpdatedBy", "lastUpdatedAt", "region", "component"];

        try {
          dispatch(addLoadingTask(TASK));

          const { auth } = getState() as RootState;

          if (!auth?.user?.uid || !organizationId || !contentId) {
            throw new Error("Invalid payload");
          }
          const docRef = doc(db, `organizations/${String(organizationId)}/content/${String(contentId)}`);

          const payload: Offer = {
            ...pick(content, ALLOWED_FIELDS),
            lastUpdatedBy: auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          const response = await updateDoc(docRef, payload as any);
          return { data: response };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Content"],
    }),
  }),
});

export const { useFetchContentQuery, useLazyFetchContentQuery, useAddContentMutation, useUpdateContentMutation, useAddOfferMutation, useUpdateOfferMutation } = contentApi;
